import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import { reApi } from "@/lib/residentApi";

function Form({ q, mine, onSaved, rid }) {
  const [ans, setAns] = useState(() => Object.fromEntries((mine?.answers || []).map((a) => [a.no, a.option])));
  const [rec, setRec] = useState(mine?.recommendation || "");
  const [notes, setNotes] = useState(mine?.notes || "");
  const [busy, setBusy] = useState(false);
  const left = q.questions.length - Object.keys(ans).length;
  const save = async () => {
    setBusy(true);
    try {
      await reApi.put(`/triage/${rid}`, { answers: Object.entries(ans).map(([no, option]) => ({ no: Number(no), option })), recommendation: rec, notes });
      toast.success("Triage assessment saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="triage-form">
      {q.questions.map((x) => (
        <div key={x.no} className="space-y-1.5" data-testid={`triage-q-${x.no}`}>
          <div className="text-sm text-slate-100">{x.no}. {x.prompt}</div>
          {x.options.map((o) => (
            <button key={o.id} type="button" onClick={() => setAns({ ...ans, [x.no]: o.id })} data-testid={`triage-q-${x.no}-${o.id}`} aria-pressed={ans[x.no] === o.id}
              className={`block w-full text-left text-xs px-2.5 py-1.5 border rounded-sm transition-colors ${ans[x.no] === o.id ? "border-amber-400 bg-amber-400/10 text-slate-100" : "border-white/10 text-slate-400 hover:border-white/30"}`}>{o.text}</button>
          ))}
        </div>
      ))}
      <div className="space-y-1.5">
        <div className="text-sm text-slate-100">Your recommendation</div>
        <div className="grid grid-cols-2 gap-1.5">{Object.entries(q.recommendations).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setRec(k)} data-testid={`triage-rec-${k}`} aria-pressed={rec === k}
            className={`text-xs px-2.5 py-2 border rounded-sm ${rec === k ? "border-amber-400 bg-amber-400/10 text-slate-100" : "border-white/10 text-slate-400"}`}>{l}</button>))}</div>
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Notes from your sit-down (optional)" data-testid="triage-notes"
        className="w-full bg-black/30 border border-white/15 rounded-sm p-2 text-sm text-slate-100" />
      <button type="button" disabled={busy || left > 0 || !rec} onClick={save} data-testid="triage-save"
        className="w-full py-2.5 rounded-sm bg-amber-400 text-black text-sm font-semibold disabled:opacity-40">{left > 0 ? `${left} questions left` : !rec ? "Pick a recommendation" : mine ? "Update my assessment" : "Save my assessment"}</button>
    </div>
  );
}

/** Step 02: the 3 PMCs' triage sit-downs for one referral — who's done, what they found, and the current PMC's own form. */
export default function TriagePanel({ referral, onBack, onChanged }) {
  const [q, setQ] = useState(null);
  const [d, setD] = useState(null);
  const load = () => reApi.get(`/triage/${referral.id}`).then((x) => { setD(x); return x; });
  useEffect(() => { reApi.get("/triage/questions").then(setQ); load(); }, [referral.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!q || !d) return <p className="mt-6 text-sm text-slate-400">Loading triage…</p>;
  const mine = d.assessments.find((a) => a.pmc.email === q.me_email) || null;
  const recs = q.recommendations;
  return (
    <div className="mt-4 space-y-5" data-testid="triage-panel">
      <button type="button" onClick={onBack} data-testid="triage-back" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-100"><ArrowLeft className="h-3.5 w-3.5" /> Back to the list</button>
      <div><div className="text-lg font-serif text-slate-100">{d.referral.first_name} {d.referral.last_name}</div><p className="text-xs text-slate-400 mt-1">{d.referral.situation_summary}</p></div>
      <div className="space-y-2" data-testid="triage-pmcs">
        {d.pmcs.map((p) => {
          const a = d.assessments.find((x) => x.pmc.email === p.email);
          return (
            <div key={p.email} data-testid={`triage-pmc-${p.email.split("@")[0]}`} className="flex items-start gap-2 text-sm">
              {a ? <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5" /> : <Circle className="h-4 w-4 text-slate-500 mt-0.5" />}
              <div><span className="text-slate-100">{p.name}</span>{a ? <span className="text-slate-400"> · score {a.score} · {recs[a.recommendation]}{a.notes ? ` · "${a.notes}"` : ""}</span> : <span className="text-slate-500"> · not yet</span>}</div>
            </div>
          );
        })}
        {d.complete && <p className="text-xs text-emerald-300" data-testid="triage-complete">All 3 PMCs are done — ready to admit and place (Step 03).</p>}
      </div>
      {q.me_pmc ? <Form key={mine?.updated_at || "new"} q={q} mine={mine} rid={referral.id} onSaved={() => { onChanged(); load().then((x) => toast.message(`${x.assessments.length} of 3 PMCs done — ${x.referral.first_name} is on ${x.complete ? "Step 03 · Intake & Placement" : "Step 02 · Triage Review"}`)); }} />
        : <p className="text-xs text-slate-500" data-testid="triage-not-pmc">Only the 3 PMCs fill in the triage assessment.</p>}
    </div>
  );
}
